import { useState, useRef, useEffect, useCallback } from 'react';
import SignaturePad from 'signature_pad';
import { Eraser, FileDown, Loader2, Truck, CheckCircle, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTransferById, useUpdateTransferStatus, StockTransfer } from '@/hooks/useStockTransfers';
import { generateStockTransferPDF, generateTransferQRCode } from '@/utils/pdfGenerator';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

interface TransferDispatchModalProps {
  transfer: StockTransfer;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TransferDispatchModal({ transfer, open, onOpenChange }: TransferDispatchModalProps) {
  const { user } = useAuth();
  const { data: detail } = useTransferById(transfer.id);
  const updateStatus = useUpdateTransferStatus();

  const [signerName, setSignerName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [canvasReady, setCanvasReady] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  // Mapeamento de qty_sent e unit_cost por item id
  const [itemValues, setItemValues] = useState<Record<string, { qty_sent: number; unit_cost: number }>>({});

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !signaturePadRef.current) return;
    const saved = signaturePadRef.current.isEmpty() ? null : signaturePadRef.current.toData();
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);
    signaturePadRef.current.clear();
    if (saved) signaturePadRef.current.fromData(saved);
  }, []);

  const setCanvasCallback = useCallback((canvas: HTMLCanvasElement | null) => {
    canvasRef.current = canvas;
    if (canvas && open) setCanvasReady(true);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setCanvasReady(false);
      setConfirmed(false);
      setSignerName('');
      setItemValues({});
      if (signaturePadRef.current) {
        signaturePadRef.current.off();
        signaturePadRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [open, resizeCanvas]);

  useEffect(() => {
    if (canvasReady && canvasRef.current && open) {
      const timer = setTimeout(() => {
        try {
          if (signaturePadRef.current) signaturePadRef.current.off();
          signaturePadRef.current = new SignaturePad(canvasRef.current!, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)',
            velocityFilterWeight: 0.7,
            minWidth: 0.5,
            maxWidth: 2.5,
          });
          resizeCanvas();
        } catch (e) {
          console.error('SignaturePad init error:', e);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [canvasReady, open, resizeCanvas]);

  // Inicializa itemValues quando o detalhe carrega
  useEffect(() => {
    if (detail?.items) {
      const initial: Record<string, { qty_sent: number; unit_cost: number }> = {};
      detail.items.forEach((item) => {
        initial[item.id] = {
          qty_sent: item.qty_sent ?? item.qty_requested,
          unit_cost: item.unit_cost ?? 0,
        };
      });
      setItemValues(initial);
    }
  }, [detail?.items]);

  const handleClear = () => signaturePadRef.current?.clear();

  const handleDispatch = async () => {
    if (!signerName.trim()) { toast.error('Informe o nome do conferente'); return; }
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      toast.error('Assinatura obrigatória');
      return;
    }
    if (!user?.id) return;

    setIsSaving(true);
    try {
      const signatureData = signaturePadRef.current.toDataURL();
      const now = new Date().toISOString();

      // Busca custo médio para itens sem custo preenchido
      const itemUpdates = detail?.items?.map((item) => {
        const vals = itemValues[item.id] ?? { qty_sent: item.qty_requested, unit_cost: 0 };
        return {
          id: item.id,
          qty_sent: vals.qty_sent,
          unit_cost: vals.unit_cost > 0 ? vals.unit_cost : undefined,
        };
      }) ?? [];

      await updateStatus.mutateAsync({
        id: transfer.id,
        status: 'em_transito',
        extra: {
          dispatched_by: user.id,
          dispatched_at: now,
          dispatch_signature_data: signatureData,
          dispatch_signature_name: signerName,
          dispatched_by_name: signerName,
        },
        items: itemUpdates,
      });

      setConfirmed(true);
    } catch {
      // erro já tratado no hook
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!detail) return;
    setIsGeneratingPDF(true);
    try {
      // Busca dados da empresa de origem
      const { data: companyData } = await supabase
        .from('companies')
        .select('name, cnpj, logo_url')
        .eq('id', transfer.company_id_from)
        .single();

      const qrCodeDataUrl = await generateTransferQRCode(transfer.id);

      const pdfBlob = await generateStockTransferPDF({
        document_number: transfer.document_number,
        created_at: transfer.created_at,
        dispatched_at: detail.dispatched_at,
        company: {
          name: companyData?.name ?? transfer.company_from?.name ?? '',
          cnpj: companyData?.cnpj ?? undefined,
          logo_url: companyData?.logo_url ?? undefined,
        },
        company_from: transfer.company_from ?? { name: '' },
        company_to: transfer.company_to ?? { name: '' },
        deposit_from: transfer.deposit_from ?? { name: '' },
        deposit_to: transfer.deposit_to ?? { name: '' },
        items: (detail.items ?? []).map((item) => ({
          product_name: item.product?.name ?? '',
          sku: item.product?.sku ?? null,
          qty_requested: item.qty_requested,
          qty_sent: item.qty_sent,
          unit_cost: item.unit_cost,
          total: item.qty_sent != null && item.unit_cost != null
            ? item.qty_sent * item.unit_cost
            : null,
        })),
        notes: detail.notes,
        dispatched_by_name: detail.dispatched_by_name,
        dispatch_signature_data: detail.dispatch_signature_data,
        qr_code_data_url: qrCodeDataUrl,
      });

      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${transfer.document_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF gerado com sucesso!');
    } catch (e) {
      console.error('Erro ao gerar PDF:', e);
      toast.error('Erro ao gerar PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const canDownloadPDF = detail?.status === 'em_transito' || detail?.status === 'recebido';

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!isSaving) { if (!v && confirmed) {} onOpenChange(v); } }}
    >
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-purple-600" />
              {confirmed ? 'Despacho Confirmado!' : `Despacho — ${transfer.document_number}`}
            </DialogTitle>
            {!isSaving && (
              <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <DialogDescription className="sr-only">
            Formulário de despacho de transferência entre unidades
          </DialogDescription>
        </DialogHeader>

        {confirmed ? (
          <div className="flex-1 flex flex-col items-center justify-center py-8 space-y-4">
            <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
              <CheckCircle className="h-8 w-8 text-purple-600" />
            </div>
            <p className="text-lg font-semibold">{transfer.document_number}</p>
            <p className="text-sm text-muted-foreground">Transferência em trânsito. Estoque NÃO foi baixado ainda.</p>
            <Button
              onClick={handleDownloadPDF}
              disabled={isGeneratingPDF}
              className="gap-2 mt-2"
              variant="outline"
            >
              {isGeneratingPDF ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              Baixar Documento PDF
            </Button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 px-1 min-h-0">
            {/* Itens com qty_sent e unit_cost editáveis */}
            <div>
              <Label className="mb-3 block text-sm font-semibold">Itens — Qtd Separada e Custo</Label>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 py-2 text-left">Produto</th>
                      <th className="px-3 py-2 text-right w-28">Qtd Solicitada</th>
                      <th className="px-3 py-2 text-right w-32">Qtd Separada</th>
                      <th className="px-3 py-2 text-right w-32">Custo Unit. R$</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detail?.items ?? []).map((item, idx) => (
                      <tr key={item.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-muted/30'}>
                        <td className="px-3 py-2">
                          <p className="font-medium">{item.product?.name}</p>
                          {item.product?.sku && (
                            <p className="text-xs text-muted-foreground font-mono">{item.product.sku}</p>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                          {item.qty_requested}
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min={0}
                            step={0.0001}
                            value={itemValues[item.id]?.qty_sent ?? item.qty_requested}
                            onChange={(e) =>
                              setItemValues((prev) => ({
                                ...prev,
                                [item.id]: {
                                  ...prev[item.id],
                                  qty_sent: parseFloat(e.target.value) || 0,
                                },
                              }))
                            }
                            className="h-8 text-right font-mono text-sm"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            value={itemValues[item.id]?.unit_cost ?? 0}
                            onChange={(e) =>
                              setItemValues((prev) => ({
                                ...prev,
                                [item.id]: {
                                  ...prev[item.id],
                                  unit_cost: parseFloat(e.target.value) || 0,
                                },
                              }))
                            }
                            className="h-8 text-right font-mono text-sm"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Nome do conferente */}
            <div className="space-y-2">
              <Label>Nome do Conferente *</Label>
              <Input
                placeholder="Nome completo do conferente"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                disabled={isSaving}
                className="h-11"
              />
            </div>

            {/* Assinatura digital — reutiliza padrão DeliverySignatureDialog */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Assinatura Digital *</Label>
                <Button type="button" variant="ghost" size="sm" onClick={handleClear} disabled={isSaving}>
                  <Eraser className="h-4 w-4 mr-1" />
                  Limpar
                </Button>
              </div>
              <div className="border-2 border-dashed border-border rounded-lg p-4">
                <canvas
                  ref={setCanvasCallback}
                  className="w-full h-32 border border-border rounded bg-white cursor-crosshair touch-none"
                  style={{ touchAction: 'none' }}
                />
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Ao assinar, a transferência muda para Em Trânsito. O estoque NÃO é baixado neste momento.
                </p>
              </div>
            </div>

            {/* Botão de PDF disponível se já está em_transito */}
            {canDownloadPDF && (
              <Button
                variant="outline"
                onClick={handleDownloadPDF}
                disabled={isGeneratingPDF}
                className="w-full gap-2"
              >
                {isGeneratingPDF ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
                Baixar Documento PDF
              </Button>
            )}
          </div>
        )}

        {!confirmed && (
          <DialogFooter className="flex-shrink-0 border-t pt-4 mt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancelar
            </Button>
            <Button
              onClick={handleDispatch}
              disabled={isSaving}
              className="gap-2 bg-purple-600 hover:bg-purple-700"
            >
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
              Confirmar Despacho
            </Button>
          </DialogFooter>
        )}

        {confirmed && (
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button className="w-full" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
